"use client";

import useSearchRouter, { ModifySearchParams } from "@/app/hooks/useSearchRouter";
import CategoryFacet from "@/app/search/facets/categories/categoryFacet";
import categoryPanelStyles from "@/app/search/facets/categories/categoryFacet.module.scss";
import style from "@/app/search/facets/genericFacet.module.scss";
import type { CategoryFacetType } from "@/app/search/utils/createUsableCategoryFacet";
import { useSearchParams } from "next/navigation";
import CaretRightIcon from "public/next-public/common/icons/caret-solid-right.svg";
import { useEffect, useState } from "react";

export default function TopLevelCategoryFacet({
  facets,
  categoryPageName,
  categoryResetSignal,
  indexName,
}: TopLevelCategoryFacetProps) {
  const parentFacet: CategoryFacetType = {
    name: "Dental Supplies",
    count: 0,
    nameForFaceting: "Dental Supplies",
    children: facets,
  };
  const searchParams = useSearchParams();
  const categoryParamValue = searchParams?.get("facet.category");
  const [selectedFacet, setSelectedFacet] = useState<CategoryFacetType | null>(
    !categoryParamValue && categoryPageName
      ? recursivelySearchForFacetByChildName(parentFacet, categoryPageName) // Case when on a category page
      : recursivelySearchForFacet(parentFacet, categoryParamValue), // Case when not on a category page
  );
  const { modifyFacets } = useSearchRouter(indexName);
  const [isExpanded, setIsExpanded] = useState(false);
  const shouldLimit = facets.length > 10;

  const setCategoryToSelected = (facet: CategoryFacetType | undefined) => {
    setSelectedFacet(facet ? facet : null);
    const categoryFacets: ModifySearchParams[] = [];
    const newFacet: ModifySearchParams = {
      facetName: "category",
      facetValue: facet?.nameForFaceting ? facet.nameForFaceting : "",
      deleteFacet: facet ? false : true,
      removeExistingOfSame: true,
    };
    categoryFacets.push(newFacet);
    modifyFacets(categoryFacets);
  };

  function handleShowMoreClick(event: React.MouseEvent<HTMLDivElement, MouseEvent>): void {
    setIsExpanded(!isExpanded);
  }

  useEffect(() => {
    // Reset both states when the signal changes
    if (categoryResetSignal && categoryResetSignal > 0) {
      setSelectedFacet(null);
    }
  }, [categoryResetSignal]);

  return (
    <div className={`facet facet-category ${style.facet} ${categoryPanelStyles.categoryFacetPanel}`}>
      <div className={style.name}>All Categories</div>
      <ul className={style.categoryFacetListContainer}>
        {facets.slice(0, shouldLimit && !isExpanded ? 10 : undefined).map((facet) => {
          return (
            <CategoryFacet
              key={facet.nameForFaceting}
              facet={facet}
              modifyCallback={setCategoryToSelected}
              selectedFacet={selectedFacet}
              categoryResetSignal={categoryResetSignal}
            />
          );
        })}
      </ul>
      {!shouldLimit ? (
        <></>
      ) : !isExpanded ? (
        <div onClick={handleShowMoreClick} className={style.showMoreLess}>
          Show More
          <CaretRightIcon width={16} height={16} style={{ transform: "rotate(90deg)" }} />
        </div>
      ) : (
        <div onClick={handleShowMoreClick} className={style.showMoreLess}>
          Show Less
          <CaretRightIcon width={16} height={16} style={{ transform: "rotate(-90deg)" }} />
        </div>
      )}
    </div>
  );
}

export type TopLevelCategoryFacetProps = {
  facets: CategoryFacetType[];
  categoryPageName?: string | undefined;
  categoryResetSignal?: number;
  indexName?: string;
};
function recursivelySearchForFacet(
  facet: CategoryFacetType,
  categoryFacetName: string | null | undefined,
): CategoryFacetType | null {
  if (facet && categoryFacetName) {
    if (facet.nameForFaceting === categoryFacetName) {
      return facet;
    } else if (facet.children) {
      for (const f of facet.children) {
        const childResult = recursivelySearchForFacet(f, categoryFacetName);
        if (childResult) {
          return childResult;
        }
      }
    }
  }
  return null;
}

function recursivelySearchForFacetByChildName(
  facet: CategoryFacetType,
  categoryFacetChildName: string | null | undefined,
): CategoryFacetType | null {
  if (facet && categoryFacetChildName) {
    if (facet.nameForFaceting.toLowerCase().endsWith(" > " + categoryFacetChildName.toLowerCase())) {
      return facet;
    } else if (facet.children) {
      for (const f of facet.children) {
        const childResult = recursivelySearchForFacetByChildName(f, categoryFacetChildName);
        if (childResult) {
          return childResult;
        }
      }
    }
  }
  return null;
}

export function isFacetXParentOfFacetY(fx: CategoryFacetType, fy: CategoryFacetType | undefined | null): boolean {
  if (fx && fy) {
    if (fx.children) {
      for (const fxChild of fx.children) {
        if (fxChild.nameForFaceting === fy.nameForFaceting) return true;
        if (recursivelySearchForFacet(fxChild, fy.nameForFaceting)) return true;
      }
    }
  }
  return false;
}
